import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Settings, Save, Loader2, IndianRupee, Calendar, Users, 
  Target, TrendingUp, AlertCircle, Archive, FileText, 
  CheckCircle, Clock, XCircle, RefreshCcw, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const formatCurrency = (value) => {
  if (!value) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '-';
  }
};

const IncentivePage = () => {
  const { token, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('configuration');
  
  // Configuration state
  const [configLoading, setConfigLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    regular_meeting_incentive: 100,
    dormant_meeting_incentive: 100,
    dormant_activation_incentive: 200,
    activation_window_days: 30,
    legacy_meeting_incentive: 0,
    brokerage_incentive_rate: 5.0
  });

  // Reports state
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportTab, setReportTab] = useState('activations');
  const [activations, setActivations] = useState([]);
  const [pending, setPending] = useState([]);
  const [expired, setExpired] = useState([]);

  useEffect(() => {
    fetchConfig();
    fetchAllReports();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/settings/incentives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig({
          regular_meeting_incentive: data.regular_meeting_incentive || 100,
          dormant_meeting_incentive: data.dormant_meeting_incentive || 100,
          dormant_activation_incentive: data.dormant_activation_incentive || 200,
          activation_window_days: data.activation_window_days || 30,
          legacy_meeting_incentive: data.legacy_meeting_incentive || 0,
          brokerage_incentive_rate: data.brokerage_incentive_rate || 5.0
        });
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
      toast.error('Failed to load incentive configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/settings/incentives`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          regular_meeting_incentive: parseInt(config.regular_meeting_incentive) || 0,
          dormant_meeting_incentive: parseInt(config.dormant_meeting_incentive) || 0,
          dormant_activation_incentive: parseInt(config.dormant_activation_incentive) || 0,
          activation_window_days: parseInt(config.activation_window_days) || 30,
          legacy_meeting_incentive: parseInt(config.legacy_meeting_incentive) || 0,
          brokerage_incentive_rate: parseFloat(config.brokerage_incentive_rate) || 5.0
        })
      });

      if (res.ok) {
        toast.success('Incentive configuration saved successfully');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save configuration');
      }
    } catch (err) {
      toast.error('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const fetchAllReports = async () => {
    setReportsLoading(true);
    try {
      const [activationsRes, pendingRes, expiredRes] = await Promise.all([
        fetch(`${API_URL}/reports/dormant-activations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/reports/pending-activations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/reports/expired-activations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (activationsRes.ok) setActivations(await activationsRes.json());
      if (pendingRes.ok) setPending(await pendingRes.json());
      if (expiredRes.ok) setExpired(await expiredRes.json());
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      toast.error('Failed to load reports');
    } finally {
      setReportsLoading(false);
    }
  };

  // Configuration Tab Content
  const ConfigurationContent = () => {
    if (configLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#C5A059] hover:bg-[#b4914e]"
            data-testid="save-config-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
        </div>

        {/* Info Banner */}
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm text-blue-300 font-medium">Incentive Rules</p>
                <ul className="text-xs text-blue-200/80 mt-1 space-y-1">
                  <li>• <strong>Regular Meeting:</strong> Applies to meetings with Active or Dormant 1 clients</li>
                  <li>• <strong>Dormant Meeting:</strong> Applies to meetings with Dormant 2 (26-59 days) or Dormant 3 (60+ days) clients</li>
                  <li>• <strong>Dormant Activation:</strong> One-time bonus when brokerage is generated within the window after a dormant meeting</li>
                  <li>• <strong>Brokerage Incentive:</strong> % of excess brokerage generated above monthly target</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Brokerage Incentive Rate */}
        <Card className="card-dark border-emerald-500/30 bg-emerald-500/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Brokerage Incentive Rate
            </CardTitle>
            <CardDescription>
              Percentage of excess brokerage above target that is paid as incentive
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="brokerage_rate">Incentive Rate (%)</Label>
                <Input
                  id="brokerage_rate"
                  type="number"
                  step="0.1"
                  value={config.brokerage_incentive_rate}
                  onChange={(e) => setConfig({ ...config, brokerage_incentive_rate: e.target.value })}
                  className="mt-1 bg-slate-800 border-white/10"
                  data-testid="brokerage-rate-input"
                />
              </div>
              <div className="flex items-end">
                <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20 w-full">
                  <p className="text-xs text-emerald-300">
                    <strong>Example:</strong> If target is ₹1,00,000 and actual is ₹1,50,000, 
                    excess is ₹50,000 × {config.brokerage_incentive_rate}% = ₹{(50000 * (parseFloat(config.brokerage_incentive_rate) || 5) / 100).toLocaleString('en-IN')} incentive
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Regular Meeting Incentive */}
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-400" />
                Regular Meeting Incentive
              </CardTitle>
              <CardDescription>
                Incentive for meetings with Active or Dormant 1 clients
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="regular_meeting">Amount per Meeting (₹)</Label>
                  <div className="relative mt-1">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="regular_meeting"
                      type="number"
                      value={config.regular_meeting_incentive}
                      onChange={(e) => setConfig({ ...config, regular_meeting_incentive: e.target.value })}
                      className="pl-10 bg-slate-800 border-white/10"
                      data-testid="regular-meeting-input"
                    />
                  </div>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <p className="text-xs text-emerald-300">
                    <strong>Current:</strong> ₹{config.regular_meeting_incentive} per meeting with active clients
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dormant Meeting Incentive */}
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-400" />
                Dormant Meeting Incentive
              </CardTitle>
              <CardDescription>
                Incentive for meetings with Dormant 2 or Dormant 3 clients
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="dormant_meeting">Amount per Meeting (₹)</Label>
                  <div className="relative mt-1">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="dormant_meeting"
                      type="number"
                      value={config.dormant_meeting_incentive}
                      onChange={(e) => setConfig({ ...config, dormant_meeting_incentive: e.target.value })}
                      className="pl-10 bg-slate-800 border-white/10"
                      data-testid="dormant-meeting-input"
                    />
                  </div>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <p className="text-xs text-amber-300">
                    <strong>Current:</strong> ₹{config.dormant_meeting_incentive} per meeting with dormant clients
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dormant Activation Incentive */}
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#C5A059]" />
                Dormant Brokerage Activation Incentive
              </CardTitle>
              <CardDescription>
                One-time bonus for re-activating a dormant client (brokerage generated)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="activation_incentive">Activation Bonus (₹)</Label>
                  <div className="relative mt-1">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="activation_incentive"
                      type="number"
                      value={config.dormant_activation_incentive}
                      onChange={(e) => setConfig({ ...config, dormant_activation_incentive: e.target.value })}
                      className="pl-10 bg-slate-800 border-white/10"
                      data-testid="activation-incentive-input"
                    />
                  </div>
                </div>
                <div className="p-3 bg-[#C5A059]/10 rounded-lg border border-[#C5A059]/20">
                  <p className="text-xs text-[#C5A059]">
                    <strong>Current:</strong> ₹{config.dormant_activation_incentive} one-time bonus per activated client
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activation Window */}
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-sky-400" />
                Activation Window
              </CardTitle>
              <CardDescription>
                Days within which brokerage must be generated after dormant meeting
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="activation_window">Window (Days)</Label>
                  <Input
                    id="activation_window"
                    type="number"
                    value={config.activation_window_days}
                    onChange={(e) => setConfig({ ...config, activation_window_days: e.target.value })}
                    className="mt-1 bg-slate-800 border-white/10"
                    data-testid="activation-window-input"
                  />
                </div>
                <div className="p-3 bg-sky-500/10 rounded-lg border border-sky-500/20">
                  <p className="text-xs text-sky-300">
                    <strong>Current:</strong> Brokerage must be recorded within {config.activation_window_days} days of meeting
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Legacy Incentive Section */}
        <Card className="card-dark border-white/5 border-dashed">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Archive className="w-5 h-5 text-slate-400" />
              Legacy Incentive (Disabled)
            </CardTitle>
            <CardDescription>
              Old ₹250 per meeting incentive - Set to 0 by default. Keep for future reuse if needed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="legacy_incentive">Legacy Meeting Incentive (₹)</Label>
                <div className="relative mt-1">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="legacy_incentive"
                    type="number"
                    value={config.legacy_meeting_incentive}
                    onChange={(e) => setConfig({ ...config, legacy_meeting_incentive: e.target.value })}
                    className="pl-10 bg-slate-800 border-white/10"
                    data-testid="legacy-incentive-input"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-600/30 w-full">
                  <p className="text-xs text-slate-400">
                    {config.legacy_meeting_incentive > 0 ? (
                      <span className="text-amber-400">⚠ Active: ₹{config.legacy_meeting_incentive} per meeting</span>
                    ) : (
                      <span>✓ Disabled (set to ₹0)</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Example Calculation */}
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white">Example Incentive Calculation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-300">Scenario: Meeting with Active Client</h4>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Regular Meeting Incentive</span>
                    <span className="text-emerald-400">₹{config.regular_meeting_incentive}</span>
                  </div>
                  <div className="border-t border-white/10 mt-2 pt-2 flex justify-between">
                    <span className="text-white font-medium">Total</span>
                    <span className="text-white font-bold">₹{config.regular_meeting_incentive}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-300">Scenario: Meeting with Dormant Client + Activation</h4>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Dormant Meeting Incentive</span>
                    <span className="text-amber-400">₹{config.dormant_meeting_incentive}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-400">Activation Bonus (if brokerage within {config.activation_window_days} days)</span>
                    <span className="text-[#C5A059]">₹{config.dormant_activation_incentive}</span>
                  </div>
                  <div className="border-t border-white/10 mt-2 pt-2 flex justify-between">
                    <span className="text-white font-medium">Maximum Total</span>
                    <span className="text-white font-bold">₹{parseInt(config.dormant_meeting_incentive || 0) + parseInt(config.dormant_activation_incentive || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Reports Tab Content
  const ReportsContent = () => {
    if (reportsLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            onClick={fetchAllReports}
            className="border-[#C5A059]/30"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-300">Completed Activations</p>
                  <p className="text-3xl font-bold text-white mt-1">{activations.length}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-300">Pending Activations</p>
                  <p className="text-3xl font-bold text-white mt-1">{pending.length}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-300">Expired (Missed)</p>
                  <p className="text-3xl font-bold text-white mt-1">{expired.length}</p>
                </div>
                <XCircle className="w-10 h-10 text-red-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Report Tabs */}
        <Tabs value={reportTab} onValueChange={setReportTab}>
          <TabsList className="bg-slate-800/50">
            <TabsTrigger value="activations" className="data-[state=active]:bg-emerald-600">
              <CheckCircle className="w-4 h-4 mr-2" />
              Completed ({activations.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-600">
              <Clock className="w-4 h-4 mr-2" />
              Pending ({pending.length})
            </TabsTrigger>
            <TabsTrigger value="expired" className="data-[state=active]:bg-red-600">
              <XCircle className="w-4 h-4 mr-2" />
              Expired ({expired.length})
            </TabsTrigger>
          </TabsList>

          {/* Completed Activations */}
          <TabsContent value="activations">
            <Card className="card-dark border-white/5">
              <CardHeader>
                <CardTitle className="text-white">Dormant Activation Report</CardTitle>
                <CardDescription>Clients successfully re-activated with brokerage within window</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-400">Client</TableHead>
                      <TableHead className="text-slate-400">Dormant Category</TableHead>
                      <TableHead className="text-slate-400">Meeting Date</TableHead>
                      <TableHead className="text-slate-400">Brokerage Date</TableHead>
                      <TableHead className="text-slate-400">Staff</TableHead>
                      <TableHead className="text-slate-400 text-right">Brokerage</TableHead>
                      <TableHead className="text-slate-400 text-right">Incentive</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                          No completed activations yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      activations.map((a, idx) => (
                        <TableRow key={idx} className="border-white/5 hover:bg-white/5">
                          <TableCell>
                            <div>
                              <span className="text-white font-medium">{a.client_name || a.client_code}</span>
                              <span className="text-slate-500 text-xs block">{a.client_code}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              a.dormant_category === 'dormant_3' ? 'bg-red-600' : 'bg-amber-600'
                            }>
                              {a.dormant_category === 'dormant_3' ? 'Dormant 3' : 'Dormant 2'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">{formatDate(a.meeting_date)}</TableCell>
                          <TableCell className="text-slate-300">{formatDate(a.brokerage_date)}</TableCell>
                          <TableCell className="text-slate-300">{a.user_name}</TableCell>
                          <TableCell className="text-right text-emerald-400">
                            {formatCurrency(a.brokerage_amount)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge className="bg-[#C5A059]">
                              {formatCurrency(a.incentive_amount)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Activations */}
          <TabsContent value="pending">
            <Card className="card-dark border-white/5">
              <CardHeader>
                <CardTitle className="text-white">Pending Dormant Activations</CardTitle>
                <CardDescription>Meeting completed, awaiting brokerage within window</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-400">Client</TableHead>
                      <TableHead className="text-slate-400">Dormant Category</TableHead>
                      <TableHead className="text-slate-400">Meeting Date</TableHead>
                      <TableHead className="text-slate-400">Deadline</TableHead>
                      <TableHead className="text-slate-400">Staff</TableHead>
                      <TableHead className="text-slate-400 text-center">Days Left</TableHead>
                      <TableHead className="text-slate-400 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pending.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                          No pending activations
                        </TableCell>
                      </TableRow>
                    ) : (
                      pending.map((p, idx) => (
                        <TableRow key={idx} className="border-white/5 hover:bg-white/5">
                          <TableCell>
                            <div>
                              <span className="text-white font-medium">{p.client_name || p.client_code}</span>
                              <span className="text-slate-500 text-xs block">{p.client_code}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              p.dormant_category === 'dormant_3' ? 'bg-red-600' : 'bg-amber-600'
                            }>
                              {p.dormant_category === 'dormant_3' ? 'Dormant 3' : 'Dormant 2'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">{formatDate(p.meeting_date)}</TableCell>
                          <TableCell className="text-slate-300">{formatDate(p.deadline_date)}</TableCell>
                          <TableCell className="text-slate-300">{p.user_name}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-bold ${
                              p.days_remaining <= 7 ? 'text-red-400' : 
                              p.days_remaining <= 14 ? 'text-yellow-400' : 'text-emerald-400'
                            }`}>
                              {p.days_remaining}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {p.days_remaining <= 7 ? (
                              <Badge className="bg-red-600">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Urgent
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-600">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Expired Activations */}
          <TabsContent value="expired">
            <Card className="card-dark border-white/5">
              <CardHeader>
                <CardTitle className="text-white">Expired Activations</CardTitle>
                <CardDescription>Meeting completed but brokerage not generated within window</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-400">Client</TableHead>
                      <TableHead className="text-slate-400">Dormant Category</TableHead>
                      <TableHead className="text-slate-400">Meeting Date</TableHead>
                      <TableHead className="text-slate-400">Deadline</TableHead>
                      <TableHead className="text-slate-400">Staff</TableHead>
                      <TableHead className="text-slate-400 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expired.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                          No expired activations
                        </TableCell>
                      </TableRow>
                    ) : (
                      expired.map((e, idx) => (
                        <TableRow key={idx} className="border-white/5 hover:bg-white/5">
                          <TableCell>
                            <div>
                              <span className="text-white font-medium">{e.client_name || e.client_code}</span>
                              <span className="text-slate-500 text-xs block">{e.client_code}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              e.dormant_category === 'dormant_3' ? 'bg-red-600' : 'bg-amber-600'
                            }>
                              {e.dormant_category === 'dormant_3' ? 'Dormant 3' : 'Dormant 2'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">{formatDate(e.meeting_date)}</TableCell>
                          <TableCell className="text-slate-300">{formatDate(e.deadline_date)}</TableCell>
                          <TableCell className="text-slate-300">{e.user_name}</TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-red-600/50 text-red-300">
                              <XCircle className="w-3 h-3 mr-1" />
                              Missed
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  // Check if user is Principal for configuration access
  const isPrincipal = hasRole(['principal']);

  return (
    <div className="space-y-6" data-testid="incentive-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <IndianRupee className="w-6 h-6 text-[#C5A059]" />
          Incentive Management
        </h1>
        <p className="text-slate-400 mt-1">Configure incentives and track dormant activations</p>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          {isPrincipal && (
            <TabsTrigger value="configuration" data-testid="tab-configuration">
              <Settings className="w-4 h-4 mr-2" />
              Configuration
            </TabsTrigger>
          )}
          <TabsTrigger value="reports" data-testid="tab-reports">
            <FileText className="w-4 h-4 mr-2" />
            Reports
          </TabsTrigger>
        </TabsList>

        {isPrincipal && (
          <TabsContent value="configuration">
            <ConfigurationContent />
          </TabsContent>
        )}

        <TabsContent value="reports">
          <ReportsContent />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IncentivePage;
